import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Empty, Form, Popconfirm, Space, Switch, Tag, Typography, message } from "antd";
import { useState } from "react";

import { clientAdvisorChatType } from "@fitness-app/client-shared/features/client-advisor";
import { type AgentSkill } from "@fitness-app/data-models";
import { AgentSkillKind } from "@fitness-app/data-models/entities/AgentSkill";

import { createSlug, type SkillFormValues } from "~/modules/Sales/SalesAgent/constants";
import { SkillEditorDrawer } from "~/modules/Sales/SalesAgent/SkillEditorDrawer";
import { supabase } from "~/store/initializeStore";

// TRE-1613 — advisor prompt resources. Skills and knowledge share agent_skill (split by
// `kind`, see AgentSkillKind), so both lists are the same CRUD with different copy.

const defaultAdvisorSkillContent = `# Nowy skill Opiekunki

## Kiedy używać
- Opisz sytuację w rozmowie z klientką, w której agent ma zastosować ten skill.

## Instrukcja dla agenta
- Dodaj konkretne kroki, wskazówki i pytania.
- Pisz tak, jak ma odpowiadać Opiekunka.
`;

const defaultAdvisorKnowledgeContent = `# Nowy wpis bazy wiedzy

## Fakty
- Opisz program / produkt / zasadę, na którą agent ma się powoływać.
- Podaj konkrety (co zawiera, dla kogo, jak działa) — bez cen, jeśli nie są stałe.
`;

const KIND_COPY: Record<
  AgentSkillKind,
  {
    addLabel: string;
    contentLabel: string;
    defaultContent: string;
    description: string;
    editTitle: string;
    empty: string;
    newTitle: string;
    title: string;
  }
> = {
  [AgentSkillKind.Skill]: {
    addLabel: "Dodaj skill",
    contentLabel: "Treść skilla (markdown)",
    defaultContent: defaultAdvisorSkillContent,
    description: "Wytyczne zachowania Opiekunki dopisywane do promptu — kolejność ma znaczenie.",
    editTitle: "Edytuj skill",
    empty: "Brak skilli Opiekunki — agent działa na bazowym prompcie.",
    newTitle: "Nowy skill",
    title: "Skille Opiekunki",
  },
  [AgentSkillKind.Knowledge]: {
    addLabel: "Dodaj wpis",
    contentLabel: "Treść wpisu (markdown)",
    defaultContent: defaultAdvisorKnowledgeContent,
    description: "Baza wiedzy o firmie, programach i produktach — Opiekunka powołuje się na nią w rozmowach.",
    editTitle: "Edytuj wpis",
    empty: "Brak wpisów — dodaj wiedzę o firmie, programach, zasadach.",
    newTitle: "Nowy wpis",
    title: "Baza wiedzy Opiekunki",
  },
};

export const AdvisorResourceList = ({ kind }: { kind: AgentSkillKind }): React.ReactElement => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SkillFormValues>();
  const [activeResource, setActiveResource] = useState<AgentSkill | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const copy = KIND_COPY[kind];
  const queryKey = ["clientAdvisorResources", kind];

  const resourcesQuery = useQuery({
    queryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agent_skill")
        .select("*")
        .eq("agentType", clientAdvisorChatType)
        .eq("kind", kind)
        .order("sortOrder", { ascending: true })
        .order("name", { ascending: true });

      if (error) throw new Error(error.message);
      return (data ?? []) as AgentSkill[];
    },
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const saveMutation = useMutation({
    mutationFn: async (values: SkillFormValues) => {
      const payload = {
        name: values.name,
        slug: values.slug || createSlug(values.name),
        description: values.description ?? "",
        content: values.content ?? "",
        enabled: values.enabled,
        sortOrder: Number(values.sortOrder ?? 0),
        updatedAt: new Date().toISOString(),
      };

      if (activeResource) {
        const { error } = await supabase.from("agent_skill").update(payload).eq("id", activeResource.id);
        if (error) throw new Error(error.message);
        return;
      }

      const { error } = await supabase
        .from("agent_skill")
        .insert({ ...payload, agentType: clientAdvisorChatType, kind });
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      setEditorOpen(false);
      setActiveResource(null);
      form.resetFields();
      await invalidate();
      void message.success("Zapisano");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się zapisać");
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ enabled, resource }: { enabled: boolean; resource: AgentSkill }) => {
      const { error } = await supabase
        .from("agent_skill")
        .update({ enabled, updatedAt: new Date().toISOString() })
        .eq("id", resource.id);

      if (error) throw new Error(error.message);
    },
    onSuccess: invalidate,
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się zmienić statusu");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (resource: AgentSkill) => {
      const { error } = await supabase.from("agent_skill").delete().eq("id", resource.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: async () => {
      await invalidate();
      void message.success("Usunięto");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się usunąć");
    },
  });

  const resources = resourcesQuery.data ?? [];
  const enabledCount = resources.filter((resource) => resource.enabled).length;

  const openNew = () => {
    setActiveResource(null);
    form.setFieldsValue({
      name: "",
      slug: "",
      description: "",
      content: copy.defaultContent,
      enabled: true,
      sortOrder: resources.length * 10 + 10,
    });
    setEditorOpen(true);
  };

  const openEdit = (resource: AgentSkill) => {
    setActiveResource(resource);
    form.setFieldsValue({
      name: resource.name,
      slug: resource.slug,
      description: resource.description,
      content: resource.content,
      enabled: resource.enabled,
      sortOrder: resource.sortOrder,
    });
    setEditorOpen(true);
  };

  const markdownValue = Form.useWatch("content", form);

  return (
    <Card
      loading={resourcesQuery.isLoading}
      title={
        <Space>
          <span>{copy.title}</span>
          <Tag color="green">{enabledCount}</Tag>
        </Space>
      }
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openNew}>
          {copy.addLabel}
        </Button>
      }
    >
      <Typography.Paragraph type="secondary" className="text-xs">
        {copy.description}
      </Typography.Paragraph>

      {resourcesQuery.isError ? (
        <Alert
          type="warning"
          showIcon
          message="Nie udało się pobrać danych"
          description={(resourcesQuery.error as Error).message}
          className="mb-3"
        />
      ) : null}

      {resources.length === 0 && !resourcesQuery.isError ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={copy.empty} />
      ) : (
        <div>
          {resources.map((resource) => (
            <div key={resource.id} className="border-b border-gray-100 py-3 last:border-b-0">
              <div className="mb-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Typography.Text strong className="block truncate">
                    {resource.name}
                  </Typography.Text>
                  <Typography.Text type="secondary" className="text-xs">
                    {resource.description || resource.slug}
                  </Typography.Text>
                </div>
                <Switch
                  size="small"
                  checked={resource.enabled}
                  loading={toggleMutation.isLoading}
                  onChange={(enabled) => toggleMutation.mutate({ enabled, resource })}
                />
              </div>
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(resource)}>
                  Edytuj
                </Button>
                <Popconfirm
                  title="Usunąć wpis?"
                  okText="Usuń"
                  cancelText="Anuluj"
                  onConfirm={() => deleteMutation.mutate(resource)}
                  disabled={deleteMutation.isLoading}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} disabled={deleteMutation.isLoading}>
                    Usuń
                  </Button>
                </Popconfirm>
              </Space>
            </div>
          ))}
        </div>
      )}

      <SkillEditorDrawer
        activeSkill={activeResource}
        contentLabel={copy.contentLabel}
        editTitle={copy.editTitle}
        form={form}
        markdownValue={markdownValue}
        newTitle={copy.newTitle}
        onClose={() => setEditorOpen(false)}
        onSubmit={(values) => saveMutation.mutate(values)}
        open={editorOpen}
        saving={saveMutation.isLoading}
      />
    </Card>
  );
};
